import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import FuzzySearch from 'fuzzy-search';
import { Search } from '@material-ui/icons';
import {
  Box,
  InputBase,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  ListSubheader,
  Typography
} from '@material-ui/core';
import { dataTypes } from '../../../views/Campaign/components/Rules/constants'; //'../Views/Campaign/components/Rules/constants';
import { useSelector } from 'react-redux';
import FilterButton from 'components/FilterButton';
import { postMessage } from 'store/helpers/helperFunctions';
import { useLanguageSettings } from 'views/Settings';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: '6px'
  },
  content: {
    padding: theme.spacing(1),
    overflow: 'auto',
    maxHeight: '437px'
  },
  key: {
    margin: theme.spacing(1)
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1),
      width: 'auto'
    }
  },
  searchIcon: {
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  inputRoot: {
    color: 'inherit'
  },
  inputInput: {
    paddingLeft: `calc(1em + ${theme.spacing(2)}px)`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch'
    }
  },
  stickyHeader: {
    '& .MuiMenu-list': {
      backgroundColor: theme.palette.background.paper
    }
  },
  productName: {
    padding: '8px',
    backgroundColor: '#E5E5E5',
    color: 'rgba(0, 0, 0, 0.87)',
    fontWeight: 500,
    margin: '8px 0px'
  },
  highlightedText: {
    fontWeight: 'bold',
    color: theme.palette.primary.main
  }
}));

const TemplateKeyBank = props => {
  const { templateKeys, dateFormatOptions, customBoxStyle, customSearchStyle, isOpen, type } = props;
  const [reservationKeysDisplay, setReservationKeysDisplay] = useState([]);
  const [fuzzySearcher, setFuzzySearcher] = useState();
  const [lastSelected, setLastSelected] = useState('');
  const [searchStr, setSearchStr] = useState('');
  const [toolTipOpen, setToolTipOpen] = useState('');
  const [hoverTooltipOpen, setHoverTooltipOpen] = useState('');
  const [selectedFilterInputs, setSelectedFilterInputs] = useState([]);
  const [anchorEl, setAnchorEl] = useState({ el: null, key: null });
  const [anchorSurveyEl, setAnchorSurveyEl] = useState({ el: null, key: null });
  const [anchorCopyEl, setAnchorCopyEl] = useState({ el: null, key: null });
  const { templateLanguageContent, getTemplateLanguageContent } = useLanguageSettings();
  const [componentContent, setComponentContent] = React.useState(null);
  const settingsMetaDataStore = useSelector(
    store => store.settings.settingsMetaData.data
  );
  const [open, setOpen] = useState(isOpen);

  const hasMultipleItinerary = (props?.templateKeys?.filter(schema => schema.enrichmentProduct === 'MULTIPLE_ITINERARIES').length > 0 && type !== "SMS");

  const classes = useStyles();

  // date formats here - https://docs.oracle.com/javase/8/docs/api/java/text/SimpleDateFormat.html
  const dateFormatMenuOptions = [
    { value: 'MM/dd/yyyy', displayExample: '04/19/2021' },
    { value: 'MM/dd/yy', displayExample: '04/19/21' },
    { value: 'MM/dd', displayExample: '04/19' },

    { value: 'dd/MM/yyyy', displayExample: '19/04/2021' },
    { value: 'dd/MM/yy', displayExample: '19/04/21' },
    { value: 'dd/MM', displayExample: '19/04' },

    { value: 'EEEE, MMMM d, yyyy', displayExample: `${componentContent?.mondayLabel || `Monday`}, ${componentContent?.aprilLabel || `April`} 21, 2021` },
    { value: 'EEEE, d MMMM, yyyy', displayExample: `${componentContent?.mondayLabel || `Monday`}, 21 ${componentContent?.aprilLabel || `April`}, 2021` },

    { value: 'MMMM d', displayExample: `${componentContent?.aprilLabel || `April`} 21` },
    { value: 'MMMM d, yyyy', displayExample: `${componentContent?.aprilLabel || `April`} 21, 2021` },
    { value: 'd MMMM, yyyy', displayExample: `21 ${componentContent?.aprilLabel || `April`}, 2021` },
    {
      value: 'MMM dd, yyyy hh:mm aaa',
      displayExample: `${componentContent?.janLabel || `Jan`} 17, 2023 01:30 PM`
    },
    {
      value: 'MMM dd, yyyy hh:mm aaa',
      displayExample: `${componentContent?.janLabel || `Jan`} 17, 2023 01:30 PM (${componentContent?.timeZoneLabel || `with time zone`})`,
      timeZone: settingsMetaDataStore?.timeZone || ''
    },
    {
      value: 'EEEE, MMM dd, yyyy hh:mm aaa',
      displayExample: `${componentContent?.mondayLabel || `Monday`}, ${componentContent?.janLabel || `Jan`} 17, 2023 01:30 PM`
    },
    {
      value: 'EEEE, MMM dd, yyyy hh:mm aaa',
      displayExample: `${componentContent?.mondayLabel || `Monday`}, ${componentContent?.janLabel || `Jan`} 17, 2023 01:30 PM (${componentContent?.timeZoneLabel || `with time zone`})`,
      timeZone: settingsMetaDataStore?.timeZone || ''
    },
    { value: 'hh:mm aaa', displayExample: '01:30 PM' },
    {
      value: 'hh:mm aaa',
      displayExample: `01:30 PM (${componentContent?.timeZoneLabel || `with time zone`})`,
      timeZone: settingsMetaDataStore?.timeZone || ''
    }
  ];

  const handleTagClick = (e, key) => {
    if (
      key.dataType === dataTypes.DATE_TIME ||
      key.dataType === dataTypes.DATE
    ) {
      openFormatOptionsMenu(e, key);
    } else if (key.dataType === 'Surveys') {
      openSurveyOptionsMenu(e, key);
    } else {
      openCopyOptionsMenu(e, key);
    }
  };

  const openFormatOptionsMenu = (event, key) => {
    setAnchorEl({ el: event.currentTarget, key });
  };

  const openSurveyOptionsMenu = (event, key) => {
    setAnchorSurveyEl({ el: event.currentTarget, key });
  };

  const openCopyOptionsMenu = (event, key) => {
    if (!hasMultipleItinerary) {
      setLastSelected(key.key);
      copyToClipboard(key.text, key.key);
      handleTooltipOpen(key.key);
    } else {
      setAnchorCopyEl({ el: event.currentTarget, key });
    }
  };

  const closeFormatOptionsMenu = () => {
    setAnchorEl({ el: null, key: null });
  };

  const closeSurveyOptionsMenu = () => {
    setAnchorSurveyEl({ el: null, key: null });
  };

  const closeCopyOptionsMenu = () => {
    setAnchorCopyEl({ el: null, key: null });
  };

  const handleFormatOptionsMenuItemClick = option => {
    closeFormatOptionsMenu();
    const { key } = anchorEl.key;
    // BE is using the follow timezone list https://garygregory.wordpress.com/2013/06/18/what-are-the-java-timezone-ids/
    const clipboardText = `{{dateFormat ${key} format="${option.value}"${
      option.timeZone ? ' tz="' + option.timeZone + '"' : ''
    }}}`;
    if (!hasMultipleItinerary) {
      setLastSelected(key);
      copyToClipboard(clipboardText, key);
      handleTooltipOpen(key);
    } else {
      setAnchorCopyEl({ el: anchorEl.el, key: anchorEl.key, value: clipboardText })
    }
  };

  const copyToClipboard = (text, key) => {
    setTimeout(handleTooltipClose, 1000);
    closeCopyOptionsMenu();
    if(window.self !== window.top) {
      postMessage({
        type: 'templateKeys',
        text: text
      });
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  const getFilteredOptions = (searchInput, selectedInputs) => {
    let fieldNames = fuzzySearcher?.search(searchInput).map(input => input.name);
    let displayKeys = templateKeys
    .filter(
      keys =>
        selectedInputs.length === 0 ||
      selectedInputs.indexOf(keys.productName) >= 0
    )
    .map(keys => ({
      ...keys,
      schemaConditionAttributes: keys.schemaConditionAttributes.filter(
        item => fieldNames.indexOf(item.name) >= 0
      )
    }));
    return displayKeys;
  };

  const handleSearch = event => {
    let str = event.target.value;
    setSearchStr(str);
    setReservationKeysDisplay(getFilteredOptions(str, selectedFilterInputs));
  };

  const filterOptions =
    templateKeys.map(option => {
      return {
        checked: selectedFilterInputs.indexOf(option.productName) >= 0,
        value: option.productName
      };
    }) || [];

  const handleFilter = event => {
    let selectedInputs = event.filter(e => e.checked).map(e => e.value);
    setSelectedFilterInputs(selectedInputs);
    setReservationKeysDisplay(getFilteredOptions(searchStr, selectedInputs));
  };

  let prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    let eventFields = [];
    templateKeys.map(data =>
      eventFields.push(...data.schemaConditionAttributes)
    );
    setFuzzySearcher(
      new FuzzySearch(eventFields, ['name'], {
        caseSensitive: false
      })
    );

    if (type === 'SMS' && open) {
      if (templateKeys.length > 0) {
      setReservationKeysDisplay(templateKeys);
        setOpen(false);
      }
    } else {
      if (isOpen) {
        setReservationKeysDisplay(templateKeys);
        setSelectedFilterInputs([]);
        setSearchStr('');
      } else {
        setReservationKeysDisplay([]);
        setSelectedFilterInputs([]);
        setSearchStr('');
      }
      prevIsOpenRef.current = isOpen;
    }
  }, [templateKeys, isOpen]);

  useEffect(() => {
    if (templateLanguageContent) {
      setComponentContent(getTemplateLanguageContent("components", "templateKeyBank"));
    }
  }, [templateLanguageContent]);

  const handleTooltipClose = () => {
    setToolTipOpen('');
    setHoverTooltipOpen('');
  };

  const handleTooltipOpen = key => {
    setToolTipOpen(key);
    setHoverTooltipOpen('');
  };

  const getHighlightedText = (text) => {
    if (searchStr.length === 0) {
      return text;
    } else {
      let searchIndex = 0;
      const highlightedText = [];
      for (let i = 0; i < text.length; i++) {
        if (searchIndex < searchStr.length && text[i].toLowerCase() === searchStr[searchIndex].toLowerCase()) {
          highlightedText.push(<span key={i} className={classes.highlightedText}>{text[i]}</span>);
          searchIndex++;
        } else {
          highlightedText.push(text[i]);
        }
      }
      return highlightedText;
    }
  };

  const getKeyComponent = key => {
    const hoverTooltipContent = (
      <div>
        <div><strong>Type:</strong> {key.dataType || '-'}</div>
        <div><strong>Description:</strong> {key.description || '-'}</div>
      </div>
    );

    return (
      <>
        <Tooltip
          key={`copy-${key.key}`}
          PopperProps={{ disablePortal: false, container: document.body }}
          onClose={handleTooltipClose}
          open={toolTipOpen === key.key}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          title={componentContent?.copiedLabel || `Copied to Clipboard`}>
          <Tooltip
            key={`hover-${key.key}`}
            PopperProps={{ disablePortal: false, container: document.body }}
            title={hoverTooltipContent}
            open={hoverTooltipOpen === key.key}
            onOpen={() => setHoverTooltipOpen(key.key)}
            onClose={() => setHoverTooltipOpen('')}
            disableFocusListener
            disableTouchListener
            disableHoverListener={toolTipOpen === key.key}>
            <Chip
              className={classes.key}
              key={key.key}
              label={getHighlightedText(key.name)}
              variant="outlined"
              color={lastSelected === key.key ? 'secondary' : 'default'}
              onClick={e => handleTagClick(e, key)}></Chip>
          </Tooltip>
        </Tooltip>
      </>
    );
  };

  const DateFormatMenu = () => {
    return (
      <Menu
        id="lock-menu"
        className={classes.stickyHeader}
        anchorEl={anchorEl.el}
        keepMounted
        open={Boolean(anchorEl.el)}
        onClose={closeFormatOptionsMenu}>
        <ListSubheader>{componentContent?.dateFormatLabel || `Choose a date format`}</ListSubheader>

        <Divider />
        {(dateFormatOptions || dateFormatMenuOptions).map((option, index) => (
          <MenuItem
            key={index}
            onClick={() => handleFormatOptionsMenuItemClick(option)}>
            <ListItemText
              primary={`Ex: ${option.displayExample}`}
              secondary={option.value}
            />
          </MenuItem>
        ))}
      </Menu>
    );
  };

  const SurveyFormatMenu = () => {
    return (
      <Menu
        id="lock-menu"
        className={classes.stickyHeader}
        anchorEl={anchorSurveyEl.el}
        keepMounted
        open={Boolean(anchorSurveyEl.el)}
        onClose={closeSurveyOptionsMenu}>
        <ListSubheader>{componentContent?.surveyLabel || `Choose a survey`}</ListSubheader>

        <Divider />
        {anchorSurveyEl?.key?.surveyMenuOptions.map(option => (
          <MenuItem
            key={option.value}
            onClick={() => {
              closeSurveyOptionsMenu();
              if (!hasMultipleItinerary) {
                setLastSelected(anchorSurveyEl.key.key);
                copyToClipboard(option.value, anchorSurveyEl.key);
                handleTooltipOpen(anchorSurveyEl.key.key);
              } else {
                setAnchorCopyEl({ el: anchorSurveyEl.el, key: anchorSurveyEl.key, value: option.value })
              }
            }}>
            <ListItemText primary={option.displayName} />
          </MenuItem>
        ))}
      </Menu>
    );
  };

  const handleCopyFormatMenuItemClick = (type) => {
    let text = anchorCopyEl.value? anchorCopyEl.value : anchorCopyEl.key.text;
    let key = anchorCopyEl.key.key;
    setLastSelected(key);
    handleTooltipOpen(key);
    // The key name will be 'surveys' but while displaying in UI the link will contain 'surveyRecipientIdentifier' 
    if (key === 'surveys') {
      key = 'surveyRecipientIdentifier'
    }
    if (type === 'copy') {
      copyToClipboard(text, key);
    } else if (type === 'copyHierarchy') {
      copyToClipboard(text.replace(key, `../${key}`), key);
    }
  }

  const CopyFormatMenu = () => {
    return (
      <Menu
        id="lock-menu"
        className={classes.stickyHeader}
        anchorEl={anchorCopyEl.el}
        keepMounted
        open={Boolean(anchorCopyEl.el)}
        onClose={closeCopyOptionsMenu}>
          <Tooltip title={`Ex: {{${componentContent?.templateKeyTooltip || `templateKey`}}}`}>
            <MenuItem onClick={(e) => handleCopyFormatMenuItemClick('copy')}>
              {componentContent?.copyKeyLabel || `Copy Key`}
            </MenuItem>
          </Tooltip>
          <Tooltip title={
            <>
              <div>{`Ex: {{../${componentContent?.templateKeyTooltip || `templateKey`}}}`}</div>
              <div>{componentContent?.copyIterationTooltip || `Note: Can be used for copying within an iteration component.`}</div>
            </>
            }>
            <MenuItem onClick={(e) => handleCopyFormatMenuItemClick('copyHierarchy')}>
              {componentContent?.copyIterationLabel || `Copy Iteration`}
            </MenuItem>
          </Tooltip>
      </Menu>
    )
  };

  return (
    <Box className={classes.root} style={{ height: '900px' }}>
      <div className={classes.search} {...(customSearchStyle && { style: customSearchStyle })}>
        <div className={classes.searchIcon}>
          <Search />
        </div>
        <InputBase
          placeholder={componentContent?.searchKeysPlaceholder || `Search keys…`}
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput
          }}
          value={searchStr}
          onChange={handleSearch}
          inputProps={{ 'aria-label': 'search' }}
        />
        {reservationKeysDisplay && templateKeys.length > 1 && (
          <FilterButton
            options={filterOptions}
            onChange={handleFilter}
            customButtonStyle={{
              margin: '0px 16px 0px 0px',
              padding: 0,
              float: 'right'
            }}
            popperPlacement="bottom-end"
            customPopperStyle={{
              zIndex: '1500',
              marginTop: '8px'
            }}
          />
        )}
      </div>
      <Divider />
      <DateFormatMenu />
      <SurveyFormatMenu />
      {hasMultipleItinerary && <CopyFormatMenu />}
      <Box
        className={classes.content}
        onScroll={handleTooltipClose}
        {...(customBoxStyle && { style: customBoxStyle })}>
        {reservationKeysDisplay.map(keys => {
          return (
            <>
              {keys.schemaConditionAttributes &&
                keys.schemaConditionAttributes.filter(key => !key.metaAttribute).length > 0 && (
                  <>
                    <Typography className={classes.productName}>
                      {keys.productName}
                    </Typography>
                    {keys.schemaConditionAttributes.filter(key => !key.metaAttribute).map(k =>
                      getKeyComponent(k)
                    )}
                  </>
                )}
            </>
          );
        })}
      </Box>
    </Box>
  );
};

export default TemplateKeyBank;
